// Access token lives in memory only — never persisted to localStorage.
// The refresh token is stored in an httpOnly cookie managed by the server.

const REFRESH_TOKEN_KEY = 'internal_rt';
const LEGACY_REFRESH_TOKEN_KEY = 'rt';
const RETURN_PATH_KEY = 'internal_portal_return_path';

type UnauthorizedHandler = () => void | Promise<void>;

// Only allow same-origin app paths — never external URLs or auth pages — so a
// stored return path can never be used to redirect off-site or into a loop.
function isSafeInternalPath(path: string | null | undefined): path is string {
  return (
    !!path &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.startsWith('/login') &&
    !path.startsWith('/auth/')
  );
}

/**
 * Remember the page the user was on before being sent to /login (e.g. after a
 * session expiry or explicit logout) so we can return them there once they sign
 * back in instead of dropping them on their role's default landing page.
 */
export function setReturnPath(path: string) {
  if (loggingOut) return;
  if (!isSafeInternalPath(path) || path === '/') return;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, path);
  } catch {
    // sessionStorage may be unavailable (private mode quota) — ignore.
  }
}

/** Read and clear the stored return path. Returns null when none/invalid. */
export function consumeReturnPath(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return isSafeInternalPath(value) ? value : null;
  } catch {
    return null;
  }
}

let onUnauthorized: UnauthorizedHandler | null = null;
let _accessToken: string | null = null;

// Set true while an explicit logout is in flight. During logout the app calls
// setUser(null), which briefly re-renders the dashboard with no user and would
// otherwise re-capture the current page as a return path before the redirect to
// /login fires. Suppress that here. A full page reload to /login clears this
// module state, so it only blocks the logout window — not later session expiry.
let loggingOut = false;

/**
 * Mark that an intentional logout has started: drop any stored return path and
 * prevent a new one from being captured until the page reloads. Call this at the
 * very start of the logout flow.
 */
export function beginLogout() {
  loggingOut = true;
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // sessionStorage may be unavailable — nothing to clear.
  }
}

export function registerUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  onUnauthorized = handler;
}

export async function triggerUnauthorized() {
  clearSession();
  if (onUnauthorized) await onUnauthorized();
}

export function getAccessToken(): string | null {
  return _accessToken;
}

/** Decode a JWT's `exp` (seconds) without verifying the signature. */
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const data = JSON.parse(atob(padded)) as { exp?: number };
    return typeof data.exp === 'number' ? data.exp : null;
  } catch {
    return null;
  }
}

/**
 * True when there is no access token, or it expires within `bufferSeconds`.
 * Used to refresh proactively before handing the token to a long-lived consumer
 * (e.g. the SignalR negotiate, which bypasses the apiRequest 401→refresh path).
 */
export function accessTokenExpiringWithin(bufferSeconds: number): boolean {
  if (!_accessToken) return true;
  const exp = decodeJwtExp(_accessToken);
  if (exp == null) return false; // undecodable — don't force a refresh loop
  return exp - Math.floor(Date.now() / 1000) <= bufferSeconds;
}

export function setSession(tokenPair: { accessToken: string; refreshToken?: string; expiresIn?: number }) {
  _accessToken = tokenPair.accessToken;
  clearStoredRefreshTokens();
}

export function clearStoredRefreshTokens() {
  try {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  } catch {
    // sessionStorage may be unavailable; access tokens are memory-only anyway.
  }
}

export function clearSession() {
  _accessToken = null;
  clearStoredRefreshTokens();
}
